/**
 * O que faz: Renderiza JSON-LD para ajudar Google e agentes a entenderem paginas publicas do Gens CRM.
 * Onde usa: Blog, hubs publicos e landing pages com conteudo indexavel.
 * Conexoes: Recebe dados de PublicContentItem e usa Helmet para inserir schema no head.
 * Tabelas: Nao interage com Supabase; schema reflete somente conteudo visivel.
 * Data: 09/05/2026
 */

import { Helmet } from "react-helmet-async";
import type { PublicContentItem } from "@/content/types";

const SITE_URL = "https://crm.agenciagens.com.br";
const ORGANIZATION_ID = `${SITE_URL}/#organization`;

type StructuredDataProps = {
  data: Record<string, unknown>;
};

export const StructuredData = ({ data }: StructuredDataProps) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "Gens CRM",
  url: SITE_URL,
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    telephone: "+55-83-99688-6297",
    areaServed: "BR",
    availableLanguage: "pt-BR",
  },
});

export const buildWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Gens CRM",
  url: SITE_URL,
});

export const buildBlogPostingSchema = (item: PublicContentItem) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: item.title,
  description: item.description,
  datePublished: item.publishedAt,
  dateModified: item.updatedAt,
  author: {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Gens CRM",
  },
  publisher: {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Gens CRM",
  },
  mainEntityOfPage: `${SITE_URL}${item.url}`,
  inLanguage: "pt-BR",
});

export const buildFaqSchema = (item: PublicContentItem) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: item.faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});
